import { FlaskConical, Tag, Truck } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Badge,
  Button,
  Card,
  ChipToggle,
  DataTable,
  EmptyState,
  useToast,
  type Column,
} from '@/components/ui';
import { useSession } from '@/features/auth/session-context';
import { useBranch } from '@/features/branch/branch-context';
import {
  useImagingReferrals,
  useLabActions,
  useLabWorklist,
} from '@/features/orders/api';
import {
  IMAGING_STATUS_LABEL,
  LAB_STATUS_LABEL,
  type ImagingReferral,
  type LabOrder,
} from '@/features/orders/types';
import { PageContainer } from '@/layouts/PageContainer';
import { isApiError } from '@/lib/api';
import { useOptionalUrlState, useUrlState } from '@/lib/url-state';
import { PatientIdentityText } from '@/pages/patients/PatientIdentityText';
import styles from '@/pages/queue/queue.module.css';
import { AppBookingsCard } from './AppBookingsCard';
import { CLINIC_TZ } from '@/lib/clinic-time';
import { ImagingDialog } from './ImagingDialog';
import { LabelsDialog } from './LabelsDialog';
import { LabOrderDrawer } from './LabOrderDrawer';
import { ResultsInboxPage } from './ResultsInboxPage';

type Filter = 'open' | 'results_ready' | 'reviewed' | 'all';
const FILTERS: { value: Filter; label: string }[] = [
  { value: 'open', label: 'To do' },
  { value: 'results_ready', label: 'With the doctor' },
  { value: 'reviewed', label: 'Released' },
  { value: 'all', label: 'All' },
];

const when = (iso: string | null) =>
  iso
    ? new Date(iso).toLocaleString('en-GB', {
        day: '2-digit',
        month: 'short',
        hour: '2-digit',
        minute: '2-digit',
        timeZone: CLINIC_TZ,
      })
    : '—';

/**
 * Admin v2 Lab "Lab orders" (artifact #113): every order with its tests and
 * status, with Labels, Sample collected and Dispatch to lab on the row; the
 * patient's app tracker moves with each step. Below, X-rays, scans and outside
 * referrals tracked until the report is in. mode="results" is the inbox.
 */
export function LabOrdersPage({
  mode = 'orders',
}: {
  mode?: 'orders' | 'results';
}) {
  if (mode === 'results') return <ResultsInboxPage />;
  return <LabOrders />;
}

function LabOrders() {
  const { t } = useTranslation();
  const { selected: branch } = useBranch();
  const { hasPermission } = useSession();
  const can = hasPermission('lab_orders:process');
  const a = useLabActions();
  const toast = useToast();
  const [filter, setFilter] = useUrlState<Filter>('filter', 'open', [
    'open',
    'results_ready',
    'reviewed',
    'all',
  ]);
  const [open, setOpen] = useOptionalUrlState('drawer');
  const [labels, setLabels] = useOptionalUrlState('labels');
  const [imaging, setImaging] = useState<ImagingReferral | null>(null);
  const { data, isLoading, error, refetch } = useLabWorklist(filter);
  const img = useImagingReferrals();
  const fail = (err: unknown) =>
    toast.show({
      tone: 'error',
      title: 'Not done',
      body: isApiError(err) ? err.message : undefined,
      error: err,
    });

  if (!branch) {
    return (
      <PageContainer>
        <p className={styles.warnNote}>
          Choose the branch you are working at in the branch switch.
        </p>
      </PageContainer>
    );
  }

  const columns: Column<LabOrder>[] = [
    {
      key: 'no',
      header: 'Order',
      render: (o) => <span className="mono">{o.number}</span>,
    },
    {
      key: 'patient',
      header: 'Patient',
      render: (o) => (
        <>
          <p className={styles.name}>{o.patient.fullName}</p>
          <p className={styles.sub}>
            <PatientIdentityText identity={o.patient.identity} />
          </p>
        </>
      ),
    },
    {
      key: 'tests',
      header: 'Tests',
      render: (o) => o.tests.map((t) => t.testName).join(', '),
    },
    {
      key: 'doctor',
      header: 'Ordered by',
      render: (o) => (
        <>
          <p>{o.orderedBy?.fullName ?? t('common.notRecorded')}</p>
          <p className={styles.sub}>{when(o.orderedAt)}</p>
        </>
      ),
    },
    {
      key: 'status',
      header: 'Status',
      render: (o) => (
        <div className={styles.badges}>
          <Badge tone={o.status === 'results_ready' ? 'warning' : 'neutral'}>
            {LAB_STATUS_LABEL[o.status]}
          </Badge>
          {o.collectionMode === 'home' && (
            <Badge tone="info">{`Home visit ${when(o.homeVisitAt)}`}</Badge>
          )}
        </div>
      ),
    },
    {
      key: 'actions',
      header: <span className="visually-hidden">Actions</span>,
      align: 'right',
      render: (o) => (
        <div className={styles.badges}>
          {can && ['ordered', 'collected', 'dispatched'].includes(o.status) && (
            <Button
              size="sm"
              icon={<Tag size={14} />}
              onClick={() => setLabels(o.id)}
            >
              Labels
            </Button>
          )}
          {can && o.status === 'ordered' && (
            <Button
              size="sm"
              variant="primary"
              icon={<FlaskConical size={14} />}
              onClick={() => void a.collect.mutateAsync(o.id).catch(fail)}
            >
              Sample collected
            </Button>
          )}
          {can && o.status === 'collected' && (
            <Button
              size="sm"
              icon={<Truck size={14} />}
              onClick={() => void a.dispatch.mutateAsync(o.id).catch(fail)}
            >
              Dispatch to lab
            </Button>
          )}
          <Button size="sm" onClick={() => setOpen(o.id)}>
            Open
          </Button>
        </div>
      ),
    },
  ];

  const imagingColumns: Column<ImagingReferral>[] = [
    {
      key: 'patient',
      header: 'Patient',
      render: (r) => <p className={styles.name}>{r.patient.fullName}</p>,
    },
    { key: 'exam', header: 'Exam', render: (r) => r.exam ?? '—' },
    { key: 'where', header: 'Referred to', render: (r) => r.referredTo ?? '—' },
    {
      key: 'when',
      header: 'Referred',
      render: (r) =>
        new Date(r.referredAt).toLocaleDateString('en-GB', {
          timeZone: CLINIC_TZ,
        }),
    },
    {
      key: 'status',
      header: 'Status',
      render: (r) => (
        <Badge tone={r.status === 'report_in' ? 'warning' : 'neutral'}>
          {IMAGING_STATUS_LABEL[r.status]}
          {r.status === 'appointment_made' && r.appointmentAt
            ? ` · ${when(r.appointmentAt)}`
            : ''}
        </Badge>
      ),
    },
    {
      key: 'actions',
      header: <span className="visually-hidden">Actions</span>,
      align: 'right',
      render: (r) => (
        <Button size="sm" onClick={() => setImaging(r)}>
          {can && r.status !== 'reviewed' ? 'Update' : 'View'}
        </Button>
      ),
    },
  ];

  return (
    <PageContainer>
      <Card
        title={`Lab orders · ${branch.name}`}
        flush
        actions={
          <ChipToggle
            label="Show"
            single
            options={FILTERS}
            selected={[filter]}
            onChange={(v) => v[0] && setFilter(v[0])}
          />
        }
      >
        <DataTable
          caption="Lab orders"
          columns={columns}
          rows={data}
          rowKey={(o) => o.id}
          loading={isLoading}
          error={error}
          onRetry={() => void refetch()}
          empty={
            <EmptyState
              title="No orders here"
              body="Orders appear when a doctor sends them from the consultation."
            />
          }
        />
        <p className={styles.sub} style={{ padding: '8px 16px' }}>
          The patient&apos;s app tracker moves with each step: sample taken, at
          the lab, results with the doctor, released.
        </p>
      </Card>
      <AppBookingsCard onOpenOrder={setOpen} />
      <Card title="X-rays, scans and outside referrals" flush>
        <DataTable
          caption="Imaging referrals"
          columns={imagingColumns}
          rows={img.data}
          rowKey={(r) => r.documentId}
          loading={img.isLoading}
          error={img.error}
          onRetry={() => void img.refetch()}
          empty={
            <EmptyState
              title="No imaging referrals"
              body="X-ray and scan referrals from the consultation are tracked here until the report is in."
            />
          }
        />
      </Card>
      <LabOrderDrawer orderId={open} onClose={() => setOpen(null)} />
      {labels && (
        <LabelsDialog orderId={labels} onClose={() => setLabels(null)} />
      )}
      {imaging && (
        <ImagingDialog referral={imaging} onClose={() => setImaging(null)} />
      )}
    </PageContainer>
  );
}
